import React, { useState } from 'react';

export function formatCommanderPlanningDate(value, preserveCalendarDay = false) {
  if (!value) return '--';
  const calendar = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(.*)$/);
  if (calendar && (!calendar[4] || preserveCalendarDay)) {
    return `${calendar[2]}/${calendar[3]}/${calendar[1]}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '--' : date.toLocaleDateString('en-US');
}

function itemDate(item, outlookStart) {
  if (item.kind !== 'plan event') return item.dueAt || item.plannedStartAt || '';
  const start = String(item.startDate || '').slice(0, 10);
  const end = String(item.endDate || '').slice(0, 10);
  return start && start < outlookStart && end >= outlookStart ? end : start;
}

export function groupCommanderPlanningItems(items, outlookStart = '') {
  const groups = [
    { key: 'HS', title: 'Home-station aircraft', items: [] },
    { key: 'OS', title: 'Deployed / offstation aircraft', items: [] },
    { key: 'Unknown', title: 'Aircraft station unknown', items: [] },
    { key: 'capacity', title: 'Organization capacity concerns', items: [] },
  ];
  (items || []).forEach(item => {
    const key = item.kind === 'labor concern' ? 'capacity' : ['HS', 'OS'].includes(item.stationState) ? item.stationState : 'Unknown';
    groups.find(group => group.key === key).items.push(item);
  });
  groups.forEach(group => group.items.sort((left, right) => {
    const leftDate = itemDate(left, outlookStart);
    const rightDate = itemDate(right, outlookStart);
    const rank = (item, when) => item.overdue ? 0 : when ? 1 : 2;
    return rank(left, leftDate) - rank(right, rightDate)
      || String(leftDate || '9999').slice(0, 10).localeCompare(String(rightDate || '9999').slice(0, 10))
      || String(left.tail || left.shop || '').localeCompare(String(right.tail || right.shop || ''));
  }));
  return groups.filter(group => group.items.length);
}

export function describeCommanderPlanningItem(item, outlookStart = '') {
  const tail = item.tail || 'Aircraft';
  if (item.kind === 'plan event') {
    const ongoing = item.startDate && item.startDate < outlookStart && item.endDate >= outlookStart;
    const current = item.eventType === 'Deployment' ? 'Currently deployed' : `${item.eventType || 'Aircraft event'} in progress`;
    const ending = item.eventType === 'Deployment' ? 'planned return' : 'planned end';
    return {
      title: `${tail} · ${ongoing ? current : item.eventType || 'Aircraft plan event'}`,
      detail: ongoing
        ? `In progress at outlook start · ${ending} ${formatCommanderPlanningDate(item.endDate)}`
        : `${formatCommanderPlanningDate(item.startDate)} to ${formatCommanderPlanningDate(item.endDate)}`,
    };
  }
  if (item.kind === 'readiness task') {
    const g081Due = ['9188-6', '8035'].includes(item.workType);
    return {
      title: `${tail} · ${item.task || 'Readiness-driving maintenance work'}`,
      detail: [
        item.overdue ? 'Overdue' : '',
        item.dueAt ? `Due ${formatCommanderPlanningDate(item.dueAt, g081Due)}` : '',
        item.plannedStartAt ? `Planned start ${formatCommanderPlanningDate(item.plannedStartAt)}` : '',
        item.missingPlannedStart ? 'Start date missing' : '',
        item.missingPlannedFinish ? 'Finish date missing' : '',
        item.missingLaborEstimate ? 'Labor estimate missing' : '',
        !item.dueAt && !item.plannedStartAt ? 'Timing not established' : '',
      ].filter(Boolean).join(' · '),
    };
  }
  const hours = value => Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
  const counts = item.stationTaskCounts || {};
  return {
    title: `${item.shop || 'Shop'} · Labor concern`,
    detail: [
      Number(item.shortfallHours) > 0 ? `${hours(item.shortfallHours)}-hour calculated shortfall` : '',
      item.signoffRisk ? '7/9-level signoff gap' : '',
      `${hours(item.plannedLaborHours)} planned vs ${hours(item.capacityHours)} available hours`,
      Number(item.unscheduledLaborHours) > 0 ? `${hours(item.unscheduledLaborHours)} hours not yet scheduled` : '',
    ].filter(Boolean).join(' · '),
    station: [
      counts.HS ? `${counts.HS} home-station work items` : '',
      counts.OS ? `${counts.OS} offstation work items` : '',
      counts.Unknown ? `${counts.Unknown} work items with station unknown` : '',
    ].filter(Boolean).join(' · '),
  };
}

export default function CommanderPlanningFocus({ focus }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  if (!focus) return null;
  const groups = groupCommanderPlanningItems(focus.items, focus.startDate);
  return (
    <section className="commander-brief-planning">
      <div className="commander-brief-planning__heading">
        <h4>Aircraft Events &amp; Maintenance Planning</h4>
        {focus.startDate && focus.endDate && <span>{formatCommanderPlanningDate(focus.startDate)} to {formatCommanderPlanningDate(focus.endDate)}</span>}
      </div>
      <p>{focus.assessment || 'Selected aircraft events, readiness-driving work, and labor concerns for the next 30 days.'}</p>
      {(focus.items || []).length ? (
        <details onToggle={(event) => setDetailsOpen(event.currentTarget.open)}>
          <summary>View {focus.items.length} priority item{focus.items.length === 1 ? '' : 's'} (up to 10)</summary>
          {detailsOpen ? (
            <div className="commander-brief-planning__details">
              <p className="commander-brief-planning__scope">Grouped by aircraft station. Overdue work appears first, then dated items from soonest to latest; undated work follows. Station does not establish where the work will occur or who will perform it.</p>
              {groups.map(group => (
                <div className="commander-brief-planning__group" key={group.key}>
                  <h5>{group.title} <span>({group.items.length})</span></h5>
                  {group.key === 'capacity' && <p className="commander-brief-planning__scope">Compared with the owning organization's capacity. Confirm support for deployed work before treating this as a home-station staffing shortfall.</p>}
                  <ol>
                    {group.items.map((item, index) => {
                      const description = describeCommanderPlanningItem(item, focus.startDate);
                      return <li key={`${item.kind}-${item.tail || item.shop}-${index}`}><strong>{description.title}</strong><span>{description.detail}</span>{description.station && <small>{description.station}</small>}</li>;
                    })}
                  </ol>
                </div>
              ))}
            </div>
          ) : null}
        </details>
      ) : <p>No near-term planning priorities were identified.</p>}
    </section>
  );
}
